import {
  Button,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type {
  CreatePerDiemPolicyDTO,
  UpdatePerDiemPolicyDTO,
} from "../../domain/contracts";

const PerDiemPolicyFormSchema = z.object({
  code: z.string().min(1, "ຕ້ອງປ້ອນລະຫັດ"),
  name: z.string().min(1, "ຕ້ອງປ້ອນຊື່"),
  amountPerDay: z.number().int().min(1),
  amountPerEvent: z.number().int().min(0).nullable().optional(),
  basis: z.enum(["per_person_per_day", "per_person_per_event"]),
  roleDancer: z.boolean(),
  roleChoreographer: z.boolean(),
  roleKol: z.boolean(),
  roleMc: z.boolean(),
  roleOther: z.boolean(),
  status: z.enum(["active", "inactive"]),
});

type PerDiemPolicyFormValues = z.infer<typeof PerDiemPolicyFormSchema>;

function roleKindsFromForm(vals: PerDiemPolicyFormValues) {
  const kinds: CreatePerDiemPolicyDTO["appliesToRoleKinds"] = [];
  if (vals.roleDancer) kinds.push("dancer");
  if (vals.roleChoreographer) kinds.push("choreographer");
  if (vals.roleKol) kinds.push("kol");
  if (vals.roleMc) kinds.push("mc");
  if (vals.roleOther) kinds.push("other");
  return kinds.length > 0 ? kinds : null;
}

function roleKindsToForm(
  kinds: string[] | null | undefined,
): Pick<
  PerDiemPolicyFormValues,
  "roleDancer" | "roleChoreographer" | "roleKol" | "roleMc" | "roleOther"
> {
  const set = new Set(kinds ?? []);
  if (!kinds || kinds.length === 0) {
    return {
      roleDancer: true,
      roleChoreographer: true,
      roleKol: false,
      roleMc: false,
      roleOther: false,
    };
  }
  return {
    roleDancer: set.has("dancer"),
    roleChoreographer: set.has("choreographer"),
    roleKol: set.has("kol"),
    roleMc: set.has("mc"),
    roleOther: set.has("other"),
  };
}

type PerDiemPolicyFormProps = {
  initialValues?: Partial<PerDiemPolicyFormValues>;
  onSubmit: (values: CreatePerDiemPolicyDTO | UpdatePerDiemPolicyDTO) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

export function PerDiemPolicyForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
}: PerDiemPolicyFormProps) {
  const roleDefaults = roleKindsToForm(
    initialValues?.roleDancer != null
      ? undefined
      : (initialValues as { appliesToRoleKinds?: string[] | null })
          ?.appliesToRoleKinds,
  );

  const methods = RHF.useForm<PerDiemPolicyFormValues>({
    resolver: zodResolver(PerDiemPolicyFormSchema),
    defaultValues: {
      code: initialValues?.code ?? "",
      name: initialValues?.name ?? "",
      amountPerDay: initialValues?.amountPerDay ?? 150_000,
      amountPerEvent: initialValues?.amountPerEvent ?? null,
      basis: initialValues?.basis ?? "per_person_per_day",
      status: initialValues?.status ?? "active",
      roleDancer: initialValues?.roleDancer ?? roleDefaults.roleDancer,
      roleChoreographer:
        initialValues?.roleChoreographer ?? roleDefaults.roleChoreographer,
      roleKol: initialValues?.roleKol ?? roleDefaults.roleKol,
      roleMc: initialValues?.roleMc ?? roleDefaults.roleMc,
      roleOther: initialValues?.roleOther ?? roleDefaults.roleOther,
    },
  });

  const basis = methods.watch("basis");

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          code: vals.code,
          name: vals.name,
          amountPerDay: vals.amountPerDay,
          amountPerEvent:
            vals.basis === "per_person_per_event"
              ? (vals.amountPerEvent ?? vals.amountPerDay)
              : null,
          basis: vals.basis,
          appliesToRoleKinds: roleKindsFromForm(vals),
          status: vals.status,
        });
      }}
      className="space-y-4 py-6"
    >
      <FormInput name="code" label="ລະຫັດ" required />
      <FormInput name="name" label="ຊື່ນະໂຍບາຍ" required />
      <FormPrice
        name="amountPerDay"
        label="ອັດຕາຕໍ່ຄົນຕໍ່ວັນ (ກີບ)"
        currencySymbol="₭"
      />
      <FormSelect
        name="basis"
        label="ວິທີຄິດ"
        options={[
          { value: "per_person_per_day", label: "ຕໍ່ຄົນ × ຈຳນວນວັນ" },
          { value: "per_person_per_event", label: "ຕໍ່ຄົນຕໍ່ງານ (flat)" },
        ]}
      />
      {basis === "per_person_per_event" && (
        <FormPrice
          name="amountPerEvent"
          label="ອັດຕາຕໍ່ຄົນຕໍ່ງານ (ກີບ)"
          currencySymbol="₭"
        />
      )}
      <div>
        <p className="mb-2 font-medium text-sm">ບົດບາດທີ່ໄດ້ຮັບ</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <FormCheckboxCard name="roleDancer" label="ນັກເຕັ້ນ" />
          <FormCheckboxCard name="roleChoreographer" label="Choreographer" />
          <FormCheckboxCard name="roleKol" label="KOL" />
          <FormCheckboxCard name="roleMc" label="MC" />
          <FormCheckboxCard name="roleOther" label="ອື່ນໆ" />
        </div>
      </div>
      <FormSelect
        name="status"
        label="ສະຖານະ"
        options={[
          { value: "active", label: "ໃຊ້ງານ" },
          { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
        ]}
      />
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
